/**
 * Herbruikbare sectie voor projectpagina — zelfde taal als onboarding / dashboard.
 */
import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

export type ProjectDetailSectionVariant = 'default' | 'muted' | 'warning' | 'highlight';

const shellClass: Record<ProjectDetailSectionVariant, string> = {
  default:
    'rounded-xl border border-border bg-surface-card p-5 shadow-sm md:p-6',
  muted: 'rounded-xl border border-border/80 bg-surface-card/90 p-5 shadow-sm md:p-6',
  warning:
    'rounded-xl border-2 border-amber-200 bg-amber-50/90 p-5 shadow-sm dark:border-amber-900 dark:bg-amber-950/35 md:p-6',
  highlight:
    'rounded-xl border border-primary/25 bg-gradient-to-br from-primary/[0.07] via-surface-card to-surface-card p-5 shadow-sm md:p-6',
};

export function ProjectDetailSection({
  id,
  icon: Icon,
  title,
  description,
  variant = 'default',
  children,
  className = '',
  headerAction,
}: {
  id?: string;
  icon: LucideIcon;
  title: string;
  description?: ReactNode;
  variant?: ProjectDetailSectionVariant;
  children: ReactNode;
  className?: string;
  headerAction?: ReactNode;
}) {
  return (
    <section id={id} className={`mb-6 scroll-mt-20 ${className}`}>
      <div className={shellClass[variant]}>
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/12 text-primary ring-1 ring-primary/20">
              <Icon className="h-5 w-5" strokeWidth={1.65} aria-hidden />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold tracking-tight text-text">{title}</h2>
              {description ? (
                <div className="mt-1 text-sm leading-relaxed text-text-muted">{description}</div>
              ) : null}
            </div>
          </div>
          {headerAction ? <div className="shrink-0 sm:pt-0.5">{headerAction}</div> : null}
        </div>
        {children}
      </div>
    </section>
  );
}

/** Horizontale “springscroll”-nav, dashboard-achtige pills */
export function ProjectDetailSectionNav({
  items,
  className = '',
}: {
  items: { href: string; label: string }[];
  className?: string;
}) {
  if (items.length === 0) return null;
  return (
    <nav
      className={`mb-6 flex gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] md:flex-wrap [&::-webkit-scrollbar]:hidden ${className}`}
      aria-label="Snel naar sectie"
    >
      {items.map((item) => (
        <a
          key={item.href}
          href={item.href}
          className="shrink-0 rounded-full border border-border/80 bg-surface px-3 py-1.5 text-xs font-medium text-text-muted shadow-sm transition hover:border-primary/30 hover:bg-primary/5 hover:text-primary sm:text-sm"
        >
          {item.label}
        </a>
      ))}
    </nav>
  );
}
